import type { ImageInfoRecord } from "@/lib/catalog/types"
import { retry } from "@/utils/retry"
import { LRU } from "@/utils/lru-cache"

const REFERENCE_WEBP_DATA_CACHE_MAX_ENTRIES = 3000
const referenceWebpDataCache = new LRU<string, Uint8Array>(REFERENCE_WEBP_DATA_CACHE_MAX_ENTRIES)
const referenceWebpDataPromiseCache = new Map<string, Promise<Uint8Array>>()

export function hasReferenceWebpData256x256Source(
	imageInfo: ImageInfoRecord | null | undefined,
): boolean {
	return Boolean(
		imageInfo?.referenceWebpData256x256Url || imageInfo?.referenceWebpData256x256?.length,
	)
}

export function createImageInfoWithoutReferenceWebpData(
	imageInfo: ImageInfoRecord,
): ImageInfoRecord {
	const imageInfoForUpload = { ...imageInfo }
	delete imageInfoForUpload.referenceWebpData256x256
	return imageInfoForUpload
}

export async function getReferenceWebpData256x256(
	imageInfo: ImageInfoRecord,
): Promise<Uint8Array | null> {
	const url = imageInfo.referenceWebpData256x256Url
	if (url) {
		return getReferenceWebpDataFromUrl(url)
	}

	return imageInfo.referenceWebpData256x256?.length ? imageInfo.referenceWebpData256x256 : null
}

export function cacheReferenceWebpData256x256(url: string | null | undefined, data: Uint8Array) {
	if (url && data.length > 0) {
		referenceWebpDataCache.set(url, data)
	}
}

async function getReferenceWebpDataFromUrl(url: string): Promise<Uint8Array> {
	const cachedData = referenceWebpDataCache.get(url)
	if (cachedData) {
		return cachedData
	}

	let dataPromise = referenceWebpDataPromiseCache.get(url)
	if (!dataPromise) {
		dataPromise = fetchReferenceWebpData(url)
			.then((data) => {
				referenceWebpDataCache.set(url, data)
				return data
			})
			.finally(() => {
				referenceWebpDataPromiseCache.delete(url)
			})
		referenceWebpDataPromiseCache.set(url, dataPromise)
	}

	return dataPromise
}

async function fetchReferenceWebpData(url: string): Promise<Uint8Array> {
	const response = await retry(async () => {
		const response = await fetch(url)
		if (!response.ok) {
			throw new Error(
				`Failed to fetch referenceWebpData256x256 from ${url}: ${response.status}`,
			)
		}
		return response
	}, [])

	return new Uint8Array(await response.arrayBuffer())
}
